import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Icon } from '../campaign-os';
import { useAuth } from '../../../contexts/AuthContext';


/**
 * CampaignOS mini sidebar (full-takeover). Ported from
 * D:\campaigning-system\src\layout\Sidebar.tsx — same brand, nav grouping
 * (Workspace / Campaigns / Management / Insights) and footer, remapped to
 * cos-* tokens and the /campaigning-tool/* routes.
 *
 * One addition that doesn't exist in the standalone prototype:
 *  - "Exit Campaigning Tool" — returns to VMS's normal app shell.
 * (The real, live Vendor Regularization feature now lives inside the Dashboard screen itself,
 *  so the separate "Live" sidebar access point was removed.)
 */
const NAV_GROUPS = [
  { section: 'Workspace', items: [{ to: '/campaigning-tool', end: true, label: 'Dashboard', icon: 'space_dashboard' }] },
  { section: 'Campaigns', items: [{ to: '/campaigning-tool/campaigns', label: 'Campaigns', icon: 'campaign' }] },
  {
    section: 'Management',
    items: [
      { to: '/campaigning-tool/templates', label: 'Templates', icon: 'dashboard_customize' },
      { to: '/campaigning-tool/audience', label: 'Vendor Audience', icon: 'groups' },
    ],
  },
  { section: 'Insights', items: [{ to: '/campaigning-tool/analytics', label: 'Analytics', icon: 'monitoring' }] },
  // What verification has cost: every run's registry calls at its rate card, plus the raw
  // per-call ledger of what reached Surepass from any screen.
  { section: 'Billing', items: [{ to: '/campaigning-tool/surepass-usage', label: 'Wallet', icon: 'fact_check' }] },
  {
    section: 'Live',
    items: [
      { to: '/campaigning-tool/broadcast', label: 'Broadcast', icon: 'campaign' },
      { to: '/campaigning-tool/regularization', label: 'Regularization', icon: 'forward_to_inbox' },
    ],
  },
];

/** A coordinator may read self-regularization runs and nothing else in here — so that is all they see. */
const COORDINATOR_GROUPS = [
  { section: 'Live', items: [{ to: '/campaigning-tool/regularization/self', label: 'Regularization', icon: 'fact_check' }] },
];

function itemClass(isActive) {
  return [
    'group relative flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-all duration-200',
    isActive
      ? 'bg-cos-teal-faint text-cos-teal-deep shadow-[0_2px_10px_rgba(15,168,155,0.12)]'
      : 'text-cos-ink-secondary hover:bg-white/55 hover:text-cos-ink',
  ].join(' ');
}

// Active-state computed here rather than via NavLink render-props: react-router-dom
// 6.0.2 does not support function-as-children on NavLink (it renders the function as
// a raw child → "Functions are not valid as a React child" → empty items).
function useIsActive() {
  const { pathname } = useLocation();
  return (to, end) => (end ? pathname === to : pathname === to || pathname.startsWith(`${to}/`));
}

const initialsOf = (name) =>
  (name || 'Admin')
    .split(/\s+/)
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

export function CampaignSidebar({ mobileOpen = false, onNavigate }) {
  const { user, isAdmin } = useAuth();
  const groups = isAdmin ? NAV_GROUPS : COORDINATOR_GROUPS;
  // Wallet alerts are gone with the Wallet screen; nothing raises a nav alert today.
  const alerts = [];
  const isActive = useIsActive();

  // Map active alerts to the nav item that resolves them, so a pill appears on the
  // relevant item (currently low-balance → Wallet). Extends automatically as the
  // wallet store surfaces more alert types.
  const alertCountByPath = alerts.reduce((acc, a) => {
    if (a.to) acc[a.to] = (acc[a.to] || 0) + 1;
    return acc;
  }, {});
  const displayName = user?.name || user?.username || user?.email || 'Administrator';
  const roleLabel = user?.role ? user.role.charAt(0).toUpperCase() + user.role.slice(1).toLowerCase() : 'Administrator';

  return (
    <>
      {/* Mobile backdrop */}
      <div
        onClick={onNavigate}
        className={`fixed inset-0 z-30 bg-[rgba(35,40,36,0.28)] backdrop-blur-[2px] transition-opacity lg:hidden ${
          mobileOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
      />
      <aside
        className={`fixed inset-y-0 left-0 z-40 flex w-[248px] flex-col border-r border-cos-line-subtle bg-[rgba(250,249,244,0.92)] backdrop-blur-[18px] transition-transform duration-300 lg:bg-[rgba(250,249,244,0.78)] lg:translate-x-0 ${
          mobileOpen ? 'translate-x-0 shadow-cos-glass-lg' : '-translate-x-full'
        }`}
      >
        {/* Brand */}
        <div className="flex items-center gap-3 px-5 pb-4 pt-6">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-cos-teal to-cos-teal-deep text-white shadow-cos-teal-glow-lg">
            <Icon name="hub" filled className="text-[22px]" weight={500} />
          </div>
          <div className="leading-tight">
            <div className="text-[15px] font-extrabold tracking-tight text-cos-ink">CampaignOS</div>
            <div className="text-2xs font-semibold uppercase tracking-[0.12em] text-cos-ink-muted">Vendor Intelligence</div>
          </div>
        </div>

        {/* Exit back to VMS */}
        <div className="px-3 pb-2">
          <Link
            to="/dashboard-overview"
            onClick={onNavigate}
            className="flex items-center gap-2.5 rounded-xl border border-cos-line bg-white/50 px-3 py-2 text-[13px] font-semibold text-cos-ink-secondary transition-colors hover:border-cos-teal/40 hover:text-cos-teal-deep"
          >
            <Icon name="arrow_back" className="text-[18px]" />
            Exit Campaigning Tool
          </Link>
        </div>

        {/* Nav — every screen is live; what a role cannot open is not listed */}
        <nav className="flex-1 space-y-5 overflow-y-auto px-3 py-2">
          {groups.map((group) => (
            <div key={group.section}>
              <div className="px-3 pb-1.5 text-2xs font-bold uppercase tracking-[0.16em] text-cos-ink-muted/80">{group.section}</div>
              <div className="space-y-1">
                {group.items.map((item) => {
                  const active = isActive(item.to, item.end);
                  // Count badge on the item whose screen has the alert (e.g. Wallet on low
                  // balance) — same pattern as VMS's "Conflicting Vendors" / "Code Conflicts".
                  const badgeCount = alertCountByPath[item.to] || 0;
                  return (
                    <Link key={item.to} to={item.to} onClick={onNavigate} className={itemClass(active)} aria-current={active ? 'page' : undefined}>
                      {active && (
                        <span className="absolute -left-3 top-1/2 h-6 w-1 -translate-y-1/2 rounded-r-full bg-cos-teal shadow-[0_0_10px_rgba(15,168,155,0.6)]" />
                      )}
                      <Icon name={item.icon} filled={active} className="text-[20px]" weight={active ? 500 : 400} />
                      <span>{item.label}</span>
                      {item.tag && (
                        <span
                          className="ml-auto rounded-full bg-cos-beige-light px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-cos-beige-ink"
                          title="Not connected to billing yet"
                        >
                          {item.tag}
                        </span>
                      )}
                      {badgeCount > 0 && (
                        <span
                          className="ml-auto flex min-w-[18px] items-center justify-center rounded-full bg-cos-status-coral px-1.5 py-0.5 text-[10px] font-bold text-white"
                          title="Attention needed"
                        >
                          {badgeCount > 99 ? '99+' : badgeCount}
                        </span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>

        {/* Footer utilities (presentational in Phase 0) */}
        <div className="space-y-1 border-t border-cos-line-subtle px-3 py-3">
          <button className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-cos-ink-secondary transition-colors hover:bg-white/55 hover:text-cos-ink">
            <Icon name="settings" className="text-[20px]" />
            Settings
          </button>
          <button className="flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-medium text-cos-ink-secondary transition-colors hover:bg-white/55 hover:text-cos-ink">
            <Icon name="help" className="text-[20px]" />
            Support
          </button>
        </div>

        {/* User (real logged-in account) */}
        <div className="m-3 mt-0 flex items-center gap-3 rounded-2xl border border-cos-line-subtle bg-white/50 px-3 py-2.5">
          <div className="grid h-9 w-9 place-items-center rounded-full bg-cos-beige-soft text-sm font-bold text-cos-beige-ink">
            {initialsOf(user?.name || user?.username)}
          </div>
          <div className="min-w-0 leading-tight">
            <div className="truncate text-sm font-semibold text-cos-ink">{displayName}</div>
            <div className="truncate text-2xs text-cos-ink-muted">{roleLabel}</div>
          </div>
          <Icon name="unfold_more" className="ml-auto text-[18px] text-cos-ink-muted" />
        </div>
      </aside>
    </>
  );
}

export default CampaignSidebar;
